import { useState } from 'react'
import { useActionDispatch } from '../../hooks/useActionDispatch'

interface CharacterSkill {
  id: string
  skill_name: string
  sb: number
  ml: number
  oml: number
  is_automatic: boolean
  source: string
  notes: string | null
}

interface Props {
  character: {
    skills: CharacterSkill[]
  }
  universalPenalty: number
  physicalPenalty: number
  characterId?: string
  campaignId?: string
  canEdit?: boolean
}

type SkillTab = 'combat' | 'physical' | 'communication' | 'craft'

// Physical & Combat skills: EML = ML - (Physical Penalty × 5)
// Communication & Craft/Lore: EML = ML - (Universal Penalty × 5)
const COMBAT_SKILLS = new Set([
  'Sword','Axe','Bow','Club','Dagger','Flail','Lance','Net','Polearm',
  'Shield','Sling','Spear','Staff','Whip','Crossbow','Unarmed','Dodge',
  'Initiative','Blowgun','Riding',
  // Weapon specialties
  'Broadsword','Shortsword','Battlesword','Falchion','Mace','Morningstar',
  'Warhammer','Shortbow','Longbow','Handaxe','Battleaxe','Javelin',
  'Quarterstaff','Pike','Halberd','Glaive','Buckler','Roundshield',
  'Taburi','Trident',
  'Armour Light','Armour Mail','Armour Harness',
])

// Map specialties to their parent skill group for display grouping
const SPECIALTY_OF: Record<string, string> = {
  'Broadsword': 'Sword', 'Shortsword': 'Sword', 'Battlesword': 'Sword', 'Falchion': 'Sword',
  'Mace': 'Club', 'Morningstar': 'Club', 'Warhammer': 'Club',
  'Quarterstaff': 'Staff',
  'Shortbow': 'Bow', 'Longbow': 'Bow',
  'Handaxe': 'Axe', 'Battleaxe': 'Axe',
  'Javelin': 'Spear', 'Trident': 'Spear',
  'Pike': 'Polearm', 'Halberd': 'Polearm', 'Glaive': 'Polearm',
  'Buckler': 'Shield', 'Roundshield': 'Shield',
  'Taburi': 'Dagger',
}
const PHYSICAL_SKILLS = new Set([
  'Climbing','Condition','Jumping','Stealth','Throwing','Swimming',
  'Acrobatics','Dancing','Legerdemain','Lockcraft','Skiing',
])
// Armour skills: show in Combat tab but penalised by universal penalty only (not EP)
const ARMOUR_SKILLS = new Set(['Armour Light','Armour Mail','Armour Harness'])
// Communication and Craft/Lore are non-physical

function guessSkillType(name: string): SkillTab {
  if (COMBAT_SKILLS.has(name)) return 'combat'
  if (PHYSICAL_SKILLS.has(name)) return 'physical'
  const communication = ['Awareness','Intrigue','Oratory','Rhetoric','Singing',
    'Acting','Lovecraft','Mental Conflict','Musician','Language','Script','Heraldry']
  if (communication.includes(name)) return 'communication'
  return 'craft'
}

function isPhysicalOrCombat(type: SkillTab): boolean {
  return type === 'combat' || type === 'physical'
}

export function SkillsPanel({ character, universalPenalty, physicalPenalty, characterId, campaignId, canEdit }: Props) {
  const { dispatch } = useActionDispatch(characterId ?? '', campaignId ?? '')
  const [tab, setTab] = useState<SkillTab>('combat')
  const [advancing, setAdvancing] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const searchLower = search.toLowerCase().trim()

  const mappedSkills = character.skills
    .map(s => {
      const type = guessSkillType(s.skill_name)
      const penalty = ARMOUR_SKILLS.has(s.skill_name)
        ? universalPenalty * 5
        : isPhysicalOrCombat(type)
          ? physicalPenalty * 5
          : universalPenalty * 5
      const eml = Math.max(5, s.ml - penalty) // Minimum EML is 05
      const parent = SPECIALTY_OF[s.skill_name] ?? null
      return { ...s, type, eml, penalty, parent }
    })
    .filter(s => searchLower
      ? s.skill_name.toLowerCase().includes(searchLower)
      : s.type === tab
    )

  // Precompute each group's ML so specialties sort with their parent, not as 0
  const groupML: Record<string, number> = {}
  for (const s of mappedSkills) {
    if (!s.parent) groupML[s.skill_name] = s.ml
  }

  const skills = mappedSkills.sort((a, b) => {
    // Group specialties under their parent: parent first, then specialties
    const aGroup = a.parent ?? a.skill_name
    const bGroup = b.parent ?? b.skill_name
    if (aGroup !== bGroup) {
      // Sort groups by highest ML — use parent ML for specialties so they stay with their group
      const aMax = groupML[aGroup] ?? a.ml
      const bMax = groupML[bGroup] ?? b.ml
      return bMax - aMax || aGroup.localeCompare(bGroup)
    }
    // Within a group: parent first, then specialties by ML
    if (a.parent && !b.parent) return 1
    if (!a.parent && b.parent) return -1
    return b.ml - a.ml
  })

  const tabs: { key: SkillTab; label: string }[] = [
    { key: 'combat', label: 'Combat' },
    { key: 'physical', label: 'Physical' },
    { key: 'communication', label: 'Comm' },
    { key: 'craft', label: 'Craft/Lore' },
  ]

  const currentPenalty = isPhysicalOrCombat(tab)
    ? physicalPenalty : universalPenalty
  const penaltyLabel = isPhysicalOrCombat(tab)
    ? 'Physical' : 'Universal'

  return (
    <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
      <div className="flex justify-between items-center mb-3 pb-2 border-b-2 border-accent/20">
        <h3 className="font-cinzel font-semibold text-sm text-accent uppercase tracking-wider">Skills</h3>
        {currentPenalty > 0 && !searchLower && (
          <span className="text-xs text-danger">
            {penaltyLabel} −{currentPenalty * 5} EML
          </span>
        )}
      </div>

      {/* Search + tabs row */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search skills…"
          className="flex-1 min-w-0 px-2 py-1 text-xs border border-parchment-dark rounded focus:outline-none focus:border-accent bg-parchment placeholder-ink-light/50"
        />
        {!searchLower && tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-2 py-1 text-xs rounded whitespace-nowrap ${
              tab === t.key
                ? 'bg-accent text-white font-bold'
                : 'border border-parchment-dark hover:bg-parchment-dark'
            }`}
          >
            {t.label}
          </button>
        ))}
        {searchLower && (
          <button onClick={() => setSearch('')} className="text-xs text-ink-light hover:text-ink px-1">✕</button>
        )}
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-parchment-dark text-left">
            <th className="py-1 text-ink-light font-normal">Skill</th>
            <th className="text-right text-ink-light font-normal">SB</th>
            <th className="text-right text-ink-light font-normal">ML</th>
            <th className="text-right font-semibold text-ink">EML</th>
            {canEdit && <th className="w-8"></th>}
          </tr>
        </thead>
        <tbody>
          {skills.length === 0 && (
            <tr><td colSpan={canEdit ? 5 : 4} className="py-3 text-ink-light text-sm">
              {searchLower ? `No skills match "${search}".` : 'No skills in this category.'}
            </td></tr>
          )}
          {skills.map((s, idx) => {
            const needsHeader = !searchLower && s.parent && !skills.some(x => x.skill_name === s.parent && !x.parent)
              && (idx === 0 || (skills[idx - 1].parent !== s.parent && skills[idx - 1].skill_name !== s.parent))
            return (
              <>
                {needsHeader && (
                  <tr key={`hdr-${s.parent}`} className="border-b border-parchment/50">
                    <td className="py-1 font-medium text-ink-light">{s.parent}</td>
                    <td colSpan={canEdit ? 4 : 3}></td>
                  </tr>
                )}
                <tr key={s.id} className={`border-b border-parchment/50 ${s.parent ? 'text-ink-light' : ''}`}>
                  <td className="py-1.5">
                    {s.parent && <span className="text-ink-light/40 mr-1 ml-3">└</span>}
                    <span className={s.parent ? '' : 'font-medium'}>{s.skill_name}</span>
                    {s.notes === 'Hobby' && <span className="text-xs text-ink-light ml-1">(Hobby)</span>}
                  </td>
                  <td className="text-right text-ink-light">{s.sb}</td>
                  <td className="text-right text-ink-light">{s.ml}</td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {/* EML bar */}
                      <div className="w-16 h-1.5 bg-parchment-dark rounded-full overflow-hidden hidden sm:block">
                        <div
                          className={`h-full rounded-full ${s.penalty > 0 ? 'bg-danger' : 'bg-accent'}`}
                          style={{ width: `${Math.min(100, s.eml)}%` }}
                        />
                      </div>
                      <span className={`font-bold tabular-nums ${s.penalty > 0 ? 'text-danger' : 'text-ink'}`}>
                        {s.eml}
                      </span>
                    </div>
                  </td>
                  {canEdit && (
                    <td className="text-right">
                      <button
                        onClick={async () => {
                          setAdvancing(s.id)
                          try { await dispatch('ADVANCE_SKILL', { skill_id: s.id, amount: 1 }) } catch { /* */ }
                          setAdvancing(null)
                        }}
                        disabled={advancing === s.id}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-parchment-dark hover:bg-parchment-dark disabled:opacity-50"
                        title={`Advance ${s.skill_name} ML by 1`}
                      >+1</button>
                    </td>
                  )}
                </tr>
              </>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
